import { useState, useEffect, useRef, useCallback } from 'react';
import { Avatar } from '@picsart/design-system/components/Avatar';
import {
  InlineButton,
  ControlSizesMD,
  ControlSizesLG,
  InlineButtonAlignments,
} from '@picsart/design-system/components/InlineButton';
import { Text, TextElementTypes } from '@picsart/design-system/components/Text';
import { Divider } from '@picsart/design-system/components/Divider';
import { IconCross } from '@picsart/design-system/foundation/icons/IconCross';
import { IconLogout } from '@picsart/design-system/foundation/icons/IconLogout';
import { IconCoins } from '@picsart/design-system/foundation/icons/IconCoins';
import { IconChevronRight } from '@picsart/design-system/foundation/icons/IconChevronRight';
import { IconSettings } from '@picsart/design-system/foundation/icons/IconSettings';
import { IconAccounts } from '@picsart/design-system/foundation/icons/IconAccounts';
import { BadgeTypes } from '@picsart/design-system/components/Badge';
import { getContext } from '@/context';
import { MINIAPP_PACKAGE_ID } from '@/consts/configs';
import { fetchUserCredits } from '@/requests/user/userProfile';
import { logout } from '@/utils/user/authActions';
import { publishSdkEvent } from '@/utils/user/publishSdkEvent';
import useStyles from './styles';

const tierToBadge = (
  tierId: string | null | undefined
): BadgeTypes | undefined => {
  if (!tierId) return undefined;
  const id = tierId.toLowerCase();
  if (id.includes('ultra')) return BadgeTypes.Ultra;
  if (id.includes('max')) return BadgeTypes.Max;
  if (id.includes('pro')) return BadgeTypes.Pro;
  if (id.includes('plus')) return BadgeTypes.Plus;
  return undefined;
};

interface UserMenuButtonProps {
  isAuthorized: boolean;
  username: string;
  avatarSrc?: string;
  tierId?: string | null;
  showUserMenu: boolean;
  onCloseApp: () => void;
  onSettingsClick?: () => void;
}

export const UserMenuButton = ({
  isAuthorized,
  username,
  avatarSrc,
  tierId,
  showUserMenu,
  onCloseApp,
  onSettingsClick,
}: UserMenuButtonProps) => {
  const classes = useStyles();
  const [menuOpen, setMenuOpen] = useState(false);
  const [credits, setCredits] = useState<number | null>(null);
  const menuRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!menuOpen || !isAuthorized) return;
    fetchUserCredits().then((res) => {
      if (res) setCredits((res.credits ?? 0) + (res.addonCredits ?? 0));
    });
  }, [menuOpen, isAuthorized]);

  useEffect(() => {
    if (!menuOpen) return;
    const handler = (e: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(e.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [menuOpen]);

  const handleLogout = useCallback(async () => {
    setMenuOpen(false);
    await logout();
  }, []);

  const handleOpenCreditsScreen = useCallback(async () => {
    try {
      const result = await getContext().handlers.publishEvent({
        event: 'OpenOfferScreen',
        data: {
          source: MINIAPP_PACKAGE_ID,
          action: MINIAPP_PACKAGE_ID,
          touchpoint: MINIAPP_PACKAGE_ID,
          is_pro_touchpoint: true,
          toolId: 'genai-studio-v2',
        },
      });
      if (result) {
        const res = await fetchUserCredits();
        if (res) setCredits((res.credits ?? 0) + (res.addonCredits ?? 0));
      }
    } catch {
      console.error('[UserMenuButton] Error opening offer screen');
    }
  }, []);

  const handleOpenRegistration = useCallback(() => {
    setMenuOpen(false);
    publishSdkEvent({
      event: 'OpenRegistrationScreen',
      data: {
        source: MINIAPP_PACKAGE_ID,
        action: MINIAPP_PACKAGE_ID,
      },
    });
  }, []);

  if (!showUserMenu) {
    return (
      <InlineButton
        data-testid="genai-user-menu-close"
        size={ControlSizesMD}
        centerIcon={IconCross}
        onClick={onCloseApp}
        aria-label="Close"
        isWithoutBorder
        isWithoutBackground
      />
    );
  }

  const avatarBadge = tierToBadge(tierId);

  return (
    <div ref={menuRef} className={classes.wrapper}>
      <button
        type="button"
        className={classes.avatarBtn}
        onClick={() => setMenuOpen((v) => !v)}
        aria-label="User menu"
      >
        <Avatar
          size={32}
          imageSrc={isAuthorized ? avatarSrc : undefined}
          badge={isAuthorized ? avatarBadge : undefined}
        />
      </button>

      {menuOpen && (
        <div className={classes.dropdown}>
          {isAuthorized && (
            <>
              <div className={classes.userCard}>
                <Avatar size={48} imageSrc={avatarSrc} badge={avatarBadge} />
                <div className={classes.userInfo}>
                  <Text
                    elementType={TextElementTypes.Span}
                    className={classes.userName}
                  >
                    {username}
                  </Text>
                  <Text
                    elementType={TextElementTypes.Span}
                    className={classes.userHandle}
                  >
                    @{username}
                  </Text>
                </div>
              </div>

              {credits !== null && (
                <>
                  <InlineButton
                    data-testid="genai-user-menu-open-credits"
                    size={ControlSizesLG}
                    startIcon={IconCoins}
                    endIcon={IconChevronRight}
                    isFullWidth
                    isWithoutBorder
                    isWithoutBackground
                    alignment={InlineButtonAlignments.Start}
                    onClick={handleOpenCreditsScreen}
                  >
                    <Text elementType={TextElementTypes.Span}>
                      {credits.toLocaleString()} credits left
                    </Text>
                  </InlineButton>
                  <Divider />
                </>
              )}
            </>
          )}

          {isAuthorized ? (
            <InlineButton
              data-testid="genai-user-menu-logout"
              size={ControlSizesLG}
              startIcon={IconLogout}
              isFullWidth
              isWithoutBorder
              isWithoutBackground
              alignment={InlineButtonAlignments.Start}
              onClick={handleLogout}
            >
              <Text elementType={TextElementTypes.Span}>Log out</Text>
            </InlineButton>
          ) : (
            <InlineButton
              data-testid="genai-user-menu-sign-in"
              size={ControlSizesLG}
              startIcon={IconAccounts}
              isFullWidth
              isWithoutBorder
              isWithoutBackground
              alignment={InlineButtonAlignments.Start}
              onClick={handleOpenRegistration}
            >
              <Text elementType={TextElementTypes.Span}>Sign in</Text>
            </InlineButton>
          )}

          {onSettingsClick && (
            <>
              <Divider />
              <InlineButton
                data-testid="genai-user-menu-settings"
                size={ControlSizesLG}
                startIcon={IconSettings}
                isFullWidth
                isWithoutBorder
                isWithoutBackground
                alignment={InlineButtonAlignments.Start}
                onClick={() => {
                  setMenuOpen(false);
                  onSettingsClick();
                }}
              >
                <Text elementType={TextElementTypes.Span}>Settings</Text>
              </InlineButton>
            </>
          )}
        </div>
      )}
    </div>
  );
};
